import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  Home,
  CreditCard,
  HelpCircle,
  MoreHorizontal,
  User,
  DollarSign,
  History,
  LogOut,
  FileText,
  Headphones,
} from "lucide-react";
import Header from "@/components/Header";

export default function BottomBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut } = useAuth();
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const [currentVersion, setCurrentVersion] = useState("1.0.0");
  const [buildTime, setBuildTime] = useState("");

  useEffect(() => {
    // Load version for display only (auto-update is handled in App.jsx)
    fetch("/version.json")
      .then((res) => res.json())
      .then((data) => {
        setCurrentVersion(data.version);
        setBuildTime(data.buildTime);
      })
      .catch(() => {});
  }, []);

  const mainMenuItems = [
    { icon: Home, label: "Home", path: "/home" },
    { icon: CreditCard, label: "Card", path: "/mycard" },
    { icon: HelpCircle, label: "Help", path: "/help" },
    {
      icon: MoreHorizontal,
      label: "More",
      action: () => setIsMoreMenuOpen(true),
    },
  ];

  const moreMenuItems = [
    [
      { icon: Home, label: "Home", path: "/home" },
      { icon: User, label: "Profile", path: "/profile" },
      // { icon: FileText, label: "KYC", path: "/kyc" },
      { icon: CreditCard, label: "My Card", path: "/mycard" },
      // { icon: DollarSign, label: "Top Up", path: "/topup" },
      { icon: History, label: "History", path: "/history" },
    ],
    [
      { icon: HelpCircle, label: "Help", path: "/help" },
      // { icon: Headphones, label: "Support", path: "/support" },
      {
        icon: LogOut,
        label: "Log out",
        action: async () => {
          await signOut();
          navigate("/login");
        },
      },
    ],
  ];

  const handleMenuClick = (item) => {
    if (item.action) {
      item.action();
    } else if (item.path) {
      navigate(item.path);
    }
  };

  const handleMoreMenuClick = (item) => {
    setIsMoreMenuOpen(false);
    handleMenuClick(item);
  };

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Bottom Navigation Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-background border-t border-border/20 z-50">
        <div className="max-w-md mx-auto flex justify-around items-center py-2">
          {mainMenuItems.map((item, index) => {
            const Icon = item.icon;
            const isMoreButton = item.label === "More";
            const active = isMoreButton ? isMoreMenuOpen : (item.path && isActive(item.path));

            return (
              <button
                key={index}
                onClick={() => handleMenuClick(item)}
                className={`flex flex-col items-center gap-2 px-6 py-1 ${
                  active ? "text-primary" : "text-foreground/60"
                }`}
              >
                <Icon className="w-6 h-6" strokeWidth={active ? 2.5 : 2} />
                <span className="text-xs font-medium">{item.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* More Menu Overlay */}
      {isMoreMenuOpen && (
        <div className="fixed inset-0 z-[100] bg-background flex flex-col pt-20">
          {/* Header */}
          <Header
            showBack={true}
            showLogo={true}
            onBackClick={() => setIsMoreMenuOpen(false)}
          />

          {/* Menu Items */}
          <div className="flex-1 pt-20">
            <div className="max-w-md mx-auto px-4 flex flex-col gap-12">
              {moreMenuItems.map((row, rowIndex) => (
                <div
                  key={rowIndex}
                  className="grid grid-cols-4 gap-6"
                >
                  {row.map((item, index) => {
                    const Icon = item.icon;
                    const isLogout = item.label === "Log out";

                    return (
                      <button
                        key={index}
                        onClick={() => handleMoreMenuClick(item)}
                        className="flex flex-col items-center gap-2 opacity-80 cursor-pointer"
                      >
                      <div className="w-12 h-12 flex items-center justify-center">
                        <Icon className="w-6 h-6" strokeWidth={2} />
                      </div>
                      <span
                        className={`text-sm ${isLogout ? "font-medium" : ""}`}
                      >
                        {item.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            ))}
            </div>
          </div>

          {/* Version Display */}
          <div className="max-w-md mx-auto w-full">
            <div className="text-right px-4 py-2 text-xs text-muted-foreground/50">
              <div>v{currentVersion}</div>
              <div className="text-[10px]">
                {buildTime && (() => {
                  const date = new Date(buildTime);
                  const month = String(date.getMonth() + 1).padStart(2, "0");
                  const day = String(date.getDate()).padStart(2, "0");
                  const hours = String(date.getHours()).padStart(2, "0");
                  const minutes = String(date.getMinutes()).padStart(2, "0");
                  const seconds = String(date.getSeconds()).padStart(2, "0");
                  return `${month}-${day} ${hours}:${minutes}:${seconds}`;
                })()}
              </div>
            </div>
          </div>

          {/* Bottom Navigation (still visible) */}
          <div className="border-t border-border/20">
            <div className="max-w-md mx-auto flex justify-around items-center py-2">
              {mainMenuItems.map((item, index) => {
                const Icon = item.icon;
                const isMoreButton = item.label === "More";
                const active = isMoreButton ? true : false;

                return (
                  <button
                    key={index}
                    onClick={() => {
                      if (isMoreButton) {
                        setIsMoreMenuOpen(false);
                      } else {
                        handleMenuClick(item);
                        setIsMoreMenuOpen(false);
                      }
                    }}
                    className={`flex flex-col items-center gap-2 px-6 py-1 ${
                      active ? "text-primary" : "text-foreground/60"
                    }`}
                  >
                    <Icon
                      className="w-6 h-6"
                      strokeWidth={active ? 2.5 : 2}
                    />
                    <span className="text-xs font-medium">{item.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
